import React, { useCallback, useState } from 'react';
import { Button } from 'reactstrap';
import { LoadingMessage, Icon } from 'js/components';
import {
  faArrowsRotate,
  faFileArrowUp,
} from '@fortawesome/pro-regular-svg-icons';

import AskNext from './AskNext';
import { promptList } from './ChatComposer';
import OrgBriefing, { ChatMarkdown, ThinkingIndicator } from './OrgBriefing';
import RegenerateBriefingIcon from './RegenerateBriefingIcon';
import styles from './OrgAnalyze.module.scss';

// The pieces of one chat turn: its parts (text, survey, offer, draft) and the
// action row under an assistant reply.

function formatDraftDate(iso) {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

// A survey proposal, written by the escalated design model and shown as its
// own card. Part-level actions (copy) belong here, on the object; the reply's
// own icon set stays at the bottom of the whole turn.
function SurveyCard({ part }) {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    navigator.clipboard?.writeText(part.proposal ?? '').then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
      () => setCopied(false)
    );
  }, [part.proposal]);

  return (
    <div className={styles.surveyCard} data-test="org-survey-card">
      <div className={styles.surveyCardHeader}>
        <span>{part.title || 'Survey proposal'}</span>
        <Button color="link" className="px-0" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <ChatMarkdown text={part.proposal} />
    </div>
  );
}

// One assistant turn, rendered part by part in the order the server produced
// them. Older messages predate `parts` and fall back to the flat `content`.
export function MessageParts({ item, onOffer, sending, renderDraft }) {
  const parts = Array.isArray(item.parts) ? item.parts : [];

  return (
    <>
      {parts.map((part, i) => {
        const key = `${part.type}-${i}`;

        if (part.type === 'text') {
          return <ChatMarkdown key={key} text={part.body} />;
        }

        if (part.type === 'survey') {
          return <SurveyCard key={key} part={part} />;
        }

        if (part.type === 'offer') {
          return (
            <div key={key} className={styles.offerRegenerate}>
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={() => onOffer(part.label)}
                disabled={sending}
              >
                Regenerate with this
              </Button>
            </div>
          );
        }

        if (part.type === 'draft') {
          return <React.Fragment key={key}>{renderDraft()}</React.Fragment>;
        }

        return null;
      })}
    </>
  );
}

// A draft created from chat. `part.ref` is resolved server-side on every read
// (lightweight: id, version, who, when, generating|ready|failed), so the card
// reflects the draft as it is now rather than when the turn was written.
// `full` is populated either from the live channel payload or fetched on
// demand once the reader expands an older, collapsed card.
export function DraftCard({
  item,
  part,
  full,
  onExpand,
  onPromote,
  promoting,
  expanded,
  stage,
  partial,
  error,
  currentUserId,
  assessmentTemplate = null,
  isRubric = false,
}) {
  const ref = part?.ref;
  if (!ref) {
    return null;
  }

  const failed =
    Boolean(error) ||
    ref.generation_status === 'failed' ||
    item.status === 'failed';
  const generating =
    !failed &&
    (ref.generation_status === 'generating' || item.status === 'generating');

  return (
    <div className={styles.draftCard} data-test="org-draft-card">
      <div className={styles.draftCardHeader}>
        <span>
          {generating ? (
            'Draft analysis · thinking'
          ) : failed ? (
            'Draft analysis · failed'
          ) : (
            <>
              Draft analysis ·{' '}
              {currentUserId != null &&
              ref.created_by_id != null &&
              String(ref.created_by_id) === String(currentUserId) ? (
                <em className={styles.versionYou}>You</em>
              ) : (
                ref.created_by_name || 'Someone'
              )}{' '}
              · {formatDraftDate(ref.inserted_at)}
            </>
          )}
        </span>
        {!generating && !failed && !expanded ? (
          <Button color="link" className="px-0" onClick={onExpand}>
            View draft
          </Button>
        ) : null}
      </div>
      {generating ? <ThinkingIndicator stage={stage} /> : null}
      {generating && partial ? (
        <OrgBriefing
          streamingDocument={partial}
          onRegenerate={null}
          assessmentTemplate={assessmentTemplate}
          isRubric={isRubric}
        />
      ) : null}
      {failed && error ? (
        <div className="text-danger" role="alert">
          That draft failed to generate: {error}
        </div>
      ) : null}
      {expanded && full && !generating ? (
        <>
          <OrgBriefing
            briefing={full}
            generating={false}
            onRegenerate={null}
            assessmentTemplate={assessmentTemplate}
            isRubric={isRubric}
          />
          {full.visibility === 'draft' ? (
            <Button
              color="primary"
              size="sm"
              className={styles.publishButton}
              onClick={onPromote}
              disabled={promoting}
            >
              <Icon icon={faFileArrowUp} />
              <span>
                {promoting ? 'Publishing…' : 'Publish as the shared analysis'}
              </span>
            </Button>
          ) : (
            <div className="text-muted small">
              Published as the shared analysis.
            </div>
          )}
        </>
      ) : null}
      {expanded && !full && !generating ? (
        <LoadingMessage>Loading draft…</LoadingMessage>
      ) : null}
    </div>
  );
}

// The action row under one assistant reply.
//
// Regenerating *this* reply lives on the turn (rotate). Update briefing
// stays on the turn that offered it. Everything after the rotate is
// conditional and labeled, because an unlabeled icon that is only sometimes
// present is unreadable.
export function MessageActions({
  item,
  prompts,
  onPick,
  onRegenerateResponse,
  regenerating,
  pendingRegenerate,
  onRegenerateBriefing,
  sending,
  updating,
}) {
  const items = promptList(prompts);

  if (item.status !== 'ready') {
    return null;
  }

  return (
    <div className={styles.messageActions}>
      <div className={styles.messageActionRow}>
        <button
          type="button"
          className={styles.actionIcon}
          aria-label="Regenerate this reply"
          title="Regenerate this reply"
          disabled={regenerating || sending}
          onClick={onRegenerateResponse}
        >
          <Icon icon={faArrowsRotate} spin={regenerating} />
        </button>

        {pendingRegenerate ? (
          <button
            type="button"
            className={styles.actionChip}
            title={pendingRegenerate.reason}
            disabled={sending || updating}
            onClick={onRegenerateBriefing}
          >
            <RegenerateBriefingIcon spin={updating} />
            <span>Update briefing</span>
          </button>
        ) : null}

        <AskNext items={items} onPick={onPick} />
      </div>
    </div>
  );
}
